/* eslint-disable jsx-a11y/label-has-for */
import React from 'react';
import Spinner from 'react-spinkit';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Money from '../../components/Money';
import InputZip from '../../components/InputZip';

const PaymentForm = ({
  amount,
  panelLabel,
  submit,
  formDisabled,
  cardNumber,
  cardExpiry,
  cardCvc,
  zipCode,
  setCardNumber,
  setCardExpiry,
  setCardCvc,
  setZipCode,
  cardBrand,
  cardBrandIcon,
  payFacInputErrors,
  hasErrors,
  payFacInputsDisabled,
  isCoveredByDiscount,
  hasApplePay,
  toggleApplePay,
  shopCountry,
  cardNumberBlur,
  cardExpiryBlur,
  cardCvcBlur,
  zipCodeBlur,
  setCardExpiryRef,
  setCardCvcRef,
  setZipCodeRef,
  submitEnabled,
}) => (
  <form
    className={cx('c-card-information u-flex u-flex-column', { 'c-card-information--discounted': isCoveredByDiscount })}
    onSubmit={submit}
  >
    <div className="c-payment-modal-heading u-mb">Payment Information</div>
    <div className="u-flex-1 u-flex u-flex-column u-justify-content-space-between">
      <div>
        <label className="c-checkout-modal__input-label u-mb" id="cardNumberInputLabel">
          <div>Card Number</div>
          <div className="PayFacElement PayFacCardNumberElement u-position-relative">
            {
              cardBrandIcon && cardBrand !== 'unknown' && (
                <span className="c-checkout-modal__card-number-brand">
                  <img src={cardBrandIcon} alt={cardBrand} />
                </span>
              )
            }
            {
              cardBrand === 'unknown' && (
                <span className="c-checkout-modal__card-number-brand c-checkout-modal__card-number-brand_default">
                  <img src={cardBrandIcon} alt={cardBrand} />
                </span>
              )
            }
            <input
              id="payFacCardNumberInput"
              placeholder="Card Number"
              value={cardNumber}
              onChange={setCardNumber}
              onBlur={cardNumberBlur}
              disabled={payFacInputsDisabled}
              className={cx({ 'is-invalid': payFacInputErrors.cardNumber })}
            />
          </div>
          {payFacInputErrors.cardNumber && (
            <div className="c-checkout-modal__input-error u-co-red">
              {payFacInputErrors.cardNumber}
            </div>
          )}
        </label>
        <div className="c-checkout-modal__input-group u-mb--">
          <label className="c-checkout-modal__input-label">
            <div>Exp. Date</div>
            <input
              id="payFacCardExpiryInput"
              placeholder="MM/YY"
              value={cardExpiry}
              onChange={setCardExpiry}
              onBlur={cardExpiryBlur}
              ref={setCardExpiryRef}
              disabled={payFacInputsDisabled}
              className={cx('PayFacElement', { 'is-invalid': payFacInputErrors.cardExpiry })}
            />
            {payFacInputErrors.cardExpiry && (
              <div className="c-checkout-modal__input-error u-co-red">
                {payFacInputErrors.cardExpiry}
              </div>
            )}
          </label>
          <label className="c-checkout-modal__input-label">
            <div>CVC</div>
            <input
              id="payFacCardCvcInput"
              placeholder="CVC"
              value={cardCvc}
              onChange={setCardCvc}
              onBlur={cardCvcBlur}
              ref={setCardCvcRef}
              disabled={payFacInputsDisabled}
              className={cx('PayFacElement', { 'is-invalid': payFacInputErrors.cardCvc })}
            />
            {payFacInputErrors.cardCvc && (
              <div className="c-checkout-modal__input-error u-co-red">
                {payFacInputErrors.cardCvc}
              </div>
            )}
          </label>
          <label className="c-checkout-modal__input-label">
            <div>ZIP / Postal</div>
            <InputZip
              country={shopCountry}
              id="payFacZipCodeInput"
              placeholder="ZIP/Postal Code"
              value={zipCode}
              onChange={setZipCode}
              disabled={formDisabled || payFacInputsDisabled}
              onBlur={zipCodeBlur}
              setRef={setZipCodeRef}
              className={cx('PayFacElement', { 'is-invalid': payFacInputErrors.zipCode })}
            />
            {payFacInputErrors.zipCode && (
              <div className="c-checkout-modal__input-error c-checkout-modal__input-error_top u-co-red">
                {payFacInputErrors.zipCode}
              </div>
            )}
          </label>
        </div>
      </div>
      {
        !isCoveredByDiscount && (
          <button className="c-button" disabled={!submitEnabled || (formDisabled || hasErrors)}>
            {
              formDisabled
                ? <Spinner name="three-bounce" className="c-checkout-modal__spinner" noFadeIn />
                : (
                  <span>
                    <span>{panelLabel}&nbsp;
                      {Number(amount) && <Money>{amount}</Money>}
                    </span>
                  </span>
                )
            }
          </button>
        )
      }
      {
        isCoveredByDiscount && (
          <button className="c-button">
            Process
          </button>
        )
      }
      {
        !isCoveredByDiscount && hasApplePay && (<button
          type="button"
          className="c-btn-link c-btn-link--small u-mt- u-co-black-3 u-font-weight-normal"
          onClick={toggleApplePay}
        >
          Or pay with Apple pay
        </button>)
      }
    </div>
  </form>
);

PaymentForm.propTypes = {
  formDisabled: PropTypes.bool.isRequired,
  amount: PropTypes.number.isRequired,
  submit: PropTypes.func.isRequired,
  panelLabel: PropTypes.string,
  setCardNumber: PropTypes.func.isRequired,
  setCardExpiry: PropTypes.func.isRequired,
  setCardCvc: PropTypes.func.isRequired,
  setZipCode: PropTypes.func.isRequired,
  cardNumber: PropTypes.string.isRequired,
  cardExpiry: PropTypes.string.isRequired,
  cardCvc: PropTypes.string.isRequired,
  zipCode: PropTypes.string.isRequired,
  cardBrand: PropTypes.string.isRequired,
  cardBrandIcon: PropTypes.string.isRequired,
  payFacInputErrors: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  hasErrors: PropTypes.bool.isRequired,
  payFacInputsDisabled: PropTypes.bool.isRequired,
  isCoveredByDiscount: PropTypes.bool.isRequired,
  hasApplePay: PropTypes.bool,
  toggleApplePay: PropTypes.func,
  shopCountry: PropTypes.string.isRequired,
  cardNumberBlur: PropTypes.func.isRequired,
  cardExpiryBlur: PropTypes.func.isRequired,
  cardCvcBlur: PropTypes.func.isRequired,
  zipCodeBlur: PropTypes.func.isRequired,
  setCardExpiryRef: PropTypes.func.isRequired,
  setCardCvcRef: PropTypes.func.isRequired,
  setZipCodeRef: PropTypes.func.isRequired,
  submitEnabled: PropTypes.bool.isRequired,
};

PaymentForm.defaultProps = {
  panelLabel: 'Pay',
  hasApplePay: true,
  toggleApplePay: () => { },
};

export default PaymentForm;



// WEBPACK FOOTER //
// ./src/app/reservation/components/PaymentFormPayFac.jsx